/**
 * Enhanced Reservations Table Component
 * World-class UX with comprehensive search, full sortability, and master channel mapping
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { getStatusBucket } from '../../utils/hostawayStatus.js';
import { formatDateOnlyET, formatDateTimeET, normalizeToYmd } from '../../utils/dateTimeET.js';
import { resolveMasterChannel } from '../../utils/masterChannelResolver.js';
import { getChannelColorVar } from '../../utils/channelColorUtils.js';
import Tooltip from '../shared/Tooltip.jsx';
import '../../styles/chart-card.css';
import '../../styles/reservation-row.css';

// Phase 2: Extract format functions outside component (pure functions, no dependencies)
const formatCurrency = (value) => {
  if (value === null || value === undefined || value === 0) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Reservations date contract: use Florida/ET calendar dates (YYYY-MM-DD). Normalize ISO timestamps to YYYY-MM-DD so table and modal never show off-by-one.
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const ymd = normalizeToYmd(dateString);
  if (ymd) return formatDateOnlyET(ymd, { short: true });
  return formatDateTimeET(dateString, { dateOnly: true, short: true });
};

const getStatusColor = (status) => {
  const statusBucket = getStatusBucket(status);
  
  if (statusBucket === 'CANCELLED') return 'var(--color-status-error-base)';
  if (statusBucket === 'INQUIRY') return 'var(--color-status-warning-base)';
  if (statusBucket === 'OWNER_STAY') return 'var(--color-status-info-base)';
  if (statusBucket === 'BOOKED') return 'var(--color-status-success-base)';
  return 'var(--color-text-tertiary)';
};

const getStatusLabel = (status) => {
  const statusBucket = getStatusBucket(status);
  if (statusBucket === 'CANCELLED') return 'Cancelled';
  if (statusBucket === 'INQUIRY') return 'Inquiry';
  if (statusBucket === 'OWNER_STAY') return 'Owner Stay';
  if (statusBucket === 'BOOKED') return 'Booked';
  return status || 'Unknown';
};

export default function ReservationsTableEnhanced({
  reservations = [],
  channels = [],
  properties = [],
  onRowClick,
  searchQuery = '',
  onSearchChange = null,
  aggregates = null,
  dateRangeLabel = '',
  sortColumn = 'check_in_date',
  sortDirection = 'asc',
  onSortChange = null,
}) {
  const { theme } = useTheme();
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Ref to track in-flight search requests (for cancellation)
  const searchAbortControllerRef = useRef(null);
  
  // Phase 2: Performance debug mode (only when ?debug_reservations_perf=1 or NODE_ENV === 'development')
  const perfDebug = typeof window !== 'undefined' && (
    new URLSearchParams(window.location.search).get('debug_reservations_perf') === '1' ||
    process.env.NODE_ENV === 'development'
  );
  
  // Phase 2: Render counter and timing for performance baseline
  const renderCountRef = useRef(0);
  const renderStartTimeRef = useRef(performance.now());
  renderCountRef.current += 1;
  
  // Note: Performance logging useEffect moved after filteredReservations is defined (see below)

  // Debounce search with cancellation of in-flight requests
  useEffect(() => {
    // Cancel any in-flight search
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }
    
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300); // 300ms debounce
    
    return () => {
      clearTimeout(timer);
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
      }
    };
  }, [searchQuery]);

  // Comprehensive search across all meaningful reservation fields
  const filteredReservations = useMemo(() => {
    let filtered = [...reservations];

    // Comprehensive search filter
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase().trim();
      filtered = filtered.filter(r => {
        // Search in guest name
        const guestName = (r.guest_name || r.guestName || '').toLowerCase();
        // Search in property name
        const propertyName = (r.property_name || r.propertyName || '').toLowerCase();
        // Search in channel (raw and master)
        const channelRaw = (r.channel || r.channel_code || '').toLowerCase();
        // Pass clc_channels and canonical to respect forced lease precedence
        const masterChannel = resolveMasterChannel(
          r.channel || '', 
          r.canonical_financial_summary?.channel_subtype || null, 
          r.status,
          r.clc_channels || null,
          r.canonical_financial_summary || null
        );
        const channelLabel = masterChannel.master_channel_label.toLowerCase();
        // Search in status
        const status = (r.status || '').toLowerCase();
        // Search in reservation ID (as string)
        const reservationId = String(r.reservation_id || r.id || '').toLowerCase();
        // Search in check-in/check-out dates (formatted)
        const checkIn = r.check_in_date || r.checkin_date || '';
        const checkOut = r.check_out_date || r.checkout_date || '';
        const checkInStr = checkIn ? formatDateOnlyET(checkIn, { short: true }).toLowerCase() : '';
        const checkOutStr = checkOut ? formatDateOnlyET(checkOut, { short: true }).toLowerCase() : '';
        // Search in revenue (as string)
        const revenue = r.financials?.ownerNet != null ? String(r.financials.ownerNet).toLowerCase() : '';
        const totalPrice = r.total_price || r.revenue ? String(r.total_price || r.revenue).toLowerCase() : '';
        // Search in nights
        const nights = r.nights ? String(r.nights).toLowerCase() : '';
        
        return guestName.includes(query) || 
               propertyName.includes(query) || 
               channelRaw.includes(query) ||
               channelLabel.includes(query) ||
               status.includes(query) ||
               reservationId.includes(query) ||
               checkInStr.includes(query) ||
               checkOutStr.includes(query) ||
               revenue.includes(query) ||
               totalPrice.includes(query) ||
               nights.includes(query);
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      if (sortColumn === 'check_in_date') {
        aVal = new Date(a.check_in_date || a.checkin_date || 0);
        bVal = new Date(b.check_in_date || b.checkin_date || 0);
      } else if (sortColumn === 'check_out_date') {
        aVal = new Date(a.check_out_date || a.checkout_date || 0);
        bVal = new Date(b.check_out_date || b.checkout_date || 0);
      } else if (sortColumn === 'guest_name') {
        aVal = (a.guest_name || a.guestName || '').toLowerCase();
        bVal = (b.guest_name || b.guestName || '').toLowerCase();
      } else if (sortColumn === 'channel') {
        const aMaster = resolveMasterChannel(
          a.channel || '', 
          a.canonical_financial_summary?.channel_subtype || null, 
          a.status,
          a.clc_channels || null,
          a.canonical_financial_summary || null
        );
        const bMaster = resolveMasterChannel(
          b.channel || '', 
          b.canonical_financial_summary?.channel_subtype || null, 
          b.status,
          b.clc_channels || null,
          b.canonical_financial_summary || null
        );
        aVal = aMaster.master_channel_label.toLowerCase();
        bVal = bMaster.master_channel_label.toLowerCase();
      } else if (sortColumn === 'nights') {
        aVal = Number(a.nights) || 0;
        bVal = Number(b.nights) || 0;
      } else if (sortColumn === 'revenue') {
        aVal = a.financials?.ownerNet != null ? Number(a.financials.ownerNet) : (Number(a.revenue) || Number(a.total_price) || 0);
        bVal = b.financials?.ownerNet != null ? Number(b.financials.ownerNet) : (Number(b.revenue) || Number(b.total_price) || 0);
      } else {
        return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [reservations, debouncedSearch, sortColumn, sortDirection]);

  // Summary: use backend aggregates when present (authoritative for ALL filtered reservations); else fallback to visible rows
  const summaryData = useMemo(() => {
    const rowCount = aggregates != null ? aggregates.reservations_count : filteredReservations.length;
    const totalNights = aggregates != null ? (aggregates.total_nights ?? 0) : filteredReservations.reduce((sum, r) => {
      const nights = Number(r.nights);
      return sum + (isNaN(nights) ? 0 : nights);
    }, 0);
    const totalOwnerNet = aggregates != null ? (aggregates.total_owner_net ?? 0) : filteredReservations.reduce((sum, r) => {
      const ownerNet = r.financials?.ownerNet != null ? Number(r.financials.ownerNet) : (r.revenue || r.total_price ? Number(r.revenue || r.total_price) : null);
      return sum + (ownerNet != null && !isNaN(ownerNet) ? ownerNet : 0);
    }, 0);
    const footerTooltip = dateRangeLabel
      ? `Sum of Owner Net for all reservations in ${dateRangeLabel}.`
      : 'Sum of Owner Net for all reservations in the selected date range.';
    return { rowCount, totalNights, totalOwnerNet, footerTooltip };
  }, [filteredReservations, aggregates, dateRangeLabel]);
  
  // Phase 2: Performance baseline logging (debug-only) - after filteredReservations is defined
  useEffect(() => {
    if (perfDebug && filteredReservations) {
      const renderTime = performance.now() - renderStartTimeRef.current;
      const rowCount = filteredReservations.length;
      console.log('[PERF_BASELINE] ReservationsTableEnhanced render:', {
        renderCount: renderCountRef.current,
        rowCount,
        renderTimeMs: renderTime.toFixed(2),
        timestamp: performance.now(),
      });
      renderStartTimeRef.current = performance.now();
    }
  }, [perfDebug, filteredReservations]);

  const handleSort = useCallback((column) => {
    if (onSortChange) {
      onSortChange(column);
    }
  }, [onSortChange]);
  
  // Phase 2: Format functions moved outside component (see top of file)
  // formatCurrency, formatDate, getStatusColor, getStatusLabel are now module-level constants

  // Get master channel info for a reservation
  const getMasterChannelInfo = useCallback((reservation) => {
    return resolveMasterChannel(
      reservation.channel || '', 
      reservation.canonical_financial_summary?.channel_subtype || null, 
      reservation.status,
      reservation.clc_channels || null,
      reservation.canonical_financial_summary || null
    );
  }, []);

  if (reservations.length === 0) {
    return (
      <div style={styles.emptyState}>
        <p style={styles.emptyText}>No reservations found for this reporting period.</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Results count (shown when searching) - Fixed above scroll container */}
      {debouncedSearch && (
        <div style={{
          ...styles.resultsCount,
          color: theme === 'dark' ? 'var(--color-text-secondary-dark)' : 'var(--color-text-secondary)',
        }}>
          {filteredReservations.length} result{filteredReservations.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Table scroll container - flex: 1, overflow: auto */}
      <div style={styles.tableScrollContainer}>
        <table style={styles.table}>
          <thead style={styles.thead}>
            <tr>
              <th 
                style={{ ...styles.th, ...styles.sortableTh }}
                onClick={() => handleSort('check_in_date')}
                className="sortable"
              >
                Check-in {sortColumn === 'check_in_date' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                style={{ ...styles.th, ...styles.sortableTh }}
                onClick={() => handleSort('check_out_date')}
                className="sortable"
              >
                Check-out {sortColumn === 'check_out_date' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                style={{ ...styles.th, ...styles.sortableTh }}
                onClick={() => handleSort('guest_name')}
                className="sortable"
              >
                Guest {sortColumn === 'guest_name' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                style={{ ...styles.th, ...styles.sortableTh }}
                onClick={() => handleSort('channel')}
                className="sortable"
              >
                Channel {sortColumn === 'channel' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                style={{ ...styles.th, ...styles.sortableTh }}
                onClick={() => handleSort('nights')}
                className="sortable"
              >
                Nights {sortColumn === 'nights' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th 
                style={{ ...styles.th, ...styles.sortableTh }}
                onClick={() => handleSort('revenue')}
                className="sortable"
              >
                Owner Net {sortColumn === 'revenue' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredReservations.map((reservation, idx) => {
              const reservationId = reservation.reservation_id || reservation.id || idx;
              const masterChannel = getMasterChannelInfo(reservation);
              const nights = reservation.nights || 'N/A';
              const ownerNet = reservation.financials?.ownerNet != null 
                ? reservation.financials.ownerNet 
                : (reservation.revenue || reservation.total_price || null);
              
              return (
                <tr
                  key={idx}
                  data-testid={`reservation-row-${reservationId}`}
                  style={styles.tr}
                  onClick={() => onRowClick && onRowClick(reservation)}
                  className={onRowClick ? 'reservation-row-clickable' : ''}
                >
                  <td style={styles.td}>
                    {formatDate(reservation.check_in_date || reservation.checkin_date)}
                  </td>
                  <td style={styles.td}>
                    {formatDate(reservation.check_out_date || reservation.checkout_date)}
                  </td>
                  <td style={styles.td}>
                    {reservation.guest_name || reservation.guestName || 'N/A'}
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.channelBadge,
                      // Use shared strict resolver (single source of truth) - do not use backend master_channel_color
                      backgroundColor: getChannelColorVar(masterChannel.master_channel_key),
                    }}>
                      {masterChannel.master_channel_label}
                    </span>
                  </td>
                  <td style={styles.td}>{nights}</td>
                  <td style={styles.td}>
                    {ownerNet != null 
                      ? formatCurrency(ownerNet)
                      : <span style={{ color: 'var(--color-text-tertiary)', fontStyle: 'italic' }}>N/A</span>
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot style={{
            ...styles.tfoot,
            backgroundColor: theme === 'dark' ? 'var(--color-bg-secondary-dark)' : 'var(--color-bg-secondary)',
            borderTop: `1px solid ${theme === 'dark' ? 'var(--color-border-medium-dark)' : 'var(--color-border-medium)'}`,
          }}>
            <tr style={styles.tfootTr}>
              <td style={styles.tfootTd}>{summaryData.rowCount} row{summaryData.rowCount !== 1 ? 's' : ''}</td>
              <td style={styles.tfootTd}></td>
              <td style={styles.tfootTd}></td>
              <td style={styles.tfootTd}></td>
              <td style={styles.tfootTd}>{summaryData.totalNights}</td>
              <td style={styles.tfootTd}>
                <Tooltip content={summaryData.footerTooltip} asButton={false}>
                  <span style={styles.summaryValue}>
                    {summaryData.totalOwnerNet > 0
                      ? formatCurrency(summaryData.totalOwnerNet)
                      : '$0'}
                  </span>
                </Tooltip>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

const styles = {
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0, // Allow flex child to shrink below content size
  },
  emptyState: {
    padding: 'var(--spacing-8)',
    textAlign: 'center',
    color: 'var(--color-text-tertiary)',
    fontSize: 'var(--font-size-sm)',
    fontStyle: 'italic',
  },
  emptyText: {
    margin: 0,
  },
  resultsCount: {
    fontSize: 'var(--font-size-xs)',
    marginTop: 'var(--spacing-2)',
    marginBottom: 'var(--spacing-2)',
    paddingLeft: 'var(--spacing-3)',
    paddingTop: 'var(--spacing-2)',
    flexShrink: 0, // Fixed above scroll container
  },
  tableScrollContainer: {
    flex: 1,
    overflow: 'auto',
    minHeight: 0, // Critical for flex child to allow scrolling
    position: 'relative',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  thead: {
    position: 'sticky',
    top: 0, // Sticky relative to scroll container, not viewport
    zIndex: 10, // Above table body rows
    backgroundColor: 'var(--color-bg-tertiary)',
  },
  th: {
    padding: 'var(--spacing-3)',
    textAlign: 'left',
    backgroundColor: 'var(--color-bg-tertiary)',
    fontWeight: 'var(--font-weight-semibold)',
    color: 'var(--color-text-primary)',
    fontSize: 'var(--font-size-sm)',
    borderBottom: '2px solid var(--color-border-medium)',
  },
  sortableTh: {
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color 0.15s ease',
  },
  tr: {
    borderBottom: '1px solid var(--color-border-light)',
  },
  td: {
    padding: 'var(--spacing-3)',
    color: 'var(--color-text-primary)',
    fontSize: 'var(--font-size-sm)',
  },
  channelBadge: {
    color: 'var(--color-text-inverse)',
    padding: 'var(--spacing-1) var(--spacing-2)',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-semibold)',
    display: 'inline-block',
  },
  statusBadge: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-semibold)',
    padding: 'var(--spacing-1) var(--spacing-2)',
    borderRadius: 'var(--radius-sm)',
    display: 'inline-block',
  },
  tfoot: {
    position: 'sticky',
    bottom: 0,
    zIndex: 10,
    boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
  },
  tfootTr: {
    borderBottom: 'none',
  },
  tfootTd: {
    padding: 'var(--spacing-3)',
    color: 'var(--color-text-primary)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-semibold)',
    borderBottom: 'none',
  },
  summaryValue: {
    color: 'var(--color-text-primary)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-semibold)',
  },
};
